import { Component, Inject, OnDestroy, OnInit, Optional, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';

import type { RoutingPipelineBlock, RoutingRuleconfig1DialogData } from './models/routing-ruleconfig1.models';
import { RoutingRuleconfig1PipelineService } from './services/routing-ruleconfig1-pipeline.service';
import { RoutingRuleconfig1StoreService } from './services/routing-ruleconfig1-store.service';
import { RoutingRuleconfig1VersionService } from './services/routing-ruleconfig1-version.service';
import { Hl7FieldCatalogService } from './services/hl7-field-catalog.service';
import { XmlFieldCatalogService } from './services/xml-field-catalog.service';
import { XmlFieldPathResolverService } from './services/xml-field-path-resolver.service';
import { RoutingRuleconfig1Hl7RulesService } from './services/routing-ruleconfig1-hl7-rules.service';
import { RoutingRuleconfig1XmlRulesService } from './services/routing-ruleconfig1-xml-rules.service';
import { RoutingRuleconfig1ExpressionService } from './services/routing-ruleconfig1-expression.service';
import {
  isHl7PersistedFieldValue,
  normalizeRoutingConversionType,
} from './utils/routing-conversion-type.util';
import { RoutingMatSelectOverlayHelper } from './utils/routing-mat-select-overlay.util';

@Component({
  selector: 'app-routing-ruleconfig1',
  templateUrl: './routing-ruleconfig1.component.html',
  styleUrls: ['./routing-ruleconfig1.component.scss'],
  providers: [
    RoutingRuleconfig1StoreService,
    RoutingRuleconfig1PipelineService,
    RoutingRuleconfig1VersionService,
    RoutingRuleconfig1XmlRulesService,
    RoutingRuleconfig1Hl7RulesService,
    RoutingRuleconfig1ExpressionService,
    Hl7FieldCatalogService,
    XmlFieldCatalogService,
    XmlFieldPathResolverService,
  ],
})
export class RoutingRuleconfig1Component implements OnInit, OnDestroy {
  @ViewChild('versionSelectRef') versionSelectRef!: MatSelect;
  private readonly matSelectOverlay = new RoutingMatSelectOverlayHelper();

  /** Resolves a persisted field path to its display label for template bindings.
   * @example fieldDisplayFn('Case/AccessionNumber') → 'Accession Number'
   */
  readonly fieldDisplayFn = (field: string) => this.resolveFieldDisplay(field);

  /**
   * Injects store, pipeline, rules, expression, and version services for the routing dialog.
   * @example new RoutingRuleconfig1Component(store, pipeline, xmlRules, hl7Rules, expression, version, xmlCatalog, { conversionType: 'hl7tojson' })
   */
  constructor(
    readonly store: RoutingRuleconfig1StoreService,
    readonly pipeline: RoutingRuleconfig1PipelineService,
    readonly xmlRules: RoutingRuleconfig1XmlRulesService,
    readonly hl7Rules: RoutingRuleconfig1Hl7RulesService,
    readonly expression: RoutingRuleconfig1ExpressionService,
    readonly version: RoutingRuleconfig1VersionService,
    private readonly xmlCatalog: XmlFieldCatalogService,
    @Optional() @Inject(MAT_DIALOG_DATA) private readonly dialogData: RoutingRuleconfig1DialogData,
    @Optional() private readonly dialogRef?: MatDialogRef<RoutingRuleconfig1Component>
  ) {}

  /**
   * Returns the HL7 or XML rules service matching the current conversion type.
   * @example store.isHl7 === true → hl7Rules; conversionType 'xmltojson' → xmlRules
   */
  get activeRules(): RoutingRuleconfig1XmlRulesService | RoutingRuleconfig1Hl7RulesService {
    return this.store.isHl7 ? this.hl7Rules : this.xmlRules;
  }

  /**
   * Initializes store state, loads field catalogs, and restores pipeline blocks from dialog data.
   * @example dialogData.conversionType 'hl7tojson' → hl7Rules.ensureLoaded(), restoreFieldDisplayMappingsFromBlocks(pipelineBlocks)
   */
  ngOnInit(): void {
    const data = this.dialogData ?? {};
    this.store.dialogData = data;
    this.store.conversionType = normalizeRoutingConversionType(data.conversionType);
    this.store.uniqueID = (data.routeUniqueID ?? '').toString();
    this.store.ruleExecutionStage = (data.ruleExecutionStage ?? '').toString();
    this.store.routingAgentId = (data.routingAgentID ?? '').toString();
    this.store.routeName = (data.routeName ?? '').toString();
    this.version.setupPageRolePermissions(data.componentname);
    if (this.store.pagerolepermisions.approvefortestaccount) {
      this.store.sourceIsTestOrganization = data.sourceIsTestOrganization === '1' ? '1' : '0';
      this.store.destinationIsTestOrganization = data.destinationIsTestOrganization === '1' ? '1' : '0';
      this.store.testOrgFlagsLoaded = true;
    }
    if (this.store.isHl7) {
      this.hl7Rules.ensureLoaded();
    } else {
      this.xmlCatalog.ensureLoaded();
    }
    this.version.init({
      routingRuleConfig: data.routingRuleConfig as unknown[] | undefined,
      ruleConfigVersions: data.ruleConfigVersions as unknown[] | undefined,
    });
    if (this.store.isHl7) {
      this.hl7Rules.restoreFieldDisplayMappingsFromBlocks(this.store.pipelineBlocks);
      this.store.patchBlocks([...this.store.pipelineBlocks]);
    }
  }

  /**
   * Maps a persisted rule field value to a human-readable label for HL7 or XML paths.
   * @example 'PID.5.1' → 'Patient Name (Family)'; 'Case/AccessionNumber' → 'Accession Number'
   */
  resolveFieldDisplay(field: string): string {
    const raw = (field ?? '').toString().trim();
    if (this.store.isHl7 || isHl7PersistedFieldValue(raw)) {
      return this.hl7Rules.toFieldDisplay(field);
    }
    return this.xmlRules.toFieldDisplay(field);
  }

  /**
   * Loads the appropriate field catalog when the user focuses a field input.
   * @example isHl7 → hl7Rules.onFieldFocus(); XML route → xmlCatalog.ensureLoaded()
   */
  onFieldFocus(): void {
    if (this.store.isHl7) {
      this.hl7Rules.onFieldFocus();
    } else {
      this.xmlCatalog.ensureLoaded();
    }
  }

  /**
   * Removes a rule from a pipeline block and renumbers remaining rules.
   * @example delete rule index 1 in block 'block-2' → rules [r1,r3] become rulenumber 1,2
   */
  onDeleteRule(event: { blockId: string; index: number }): void {
    const block = this.store.pipelineBlocks.find((b) => b.id === event.blockId);
    if (!block) {
      return;
    }
    block.rules.splice(event.index, 1);
    block.rules.forEach((r, i) => {
      r.rulenumber = i + 1;
    });
    this.store.patchBlocks([...this.store.pipelineBlocks]);
  }

  /**
   * Removes a pipeline block and selects the next available block if needed.
   * @example delete block id 'pipe-a' when selected → selectedPipelineId becomes pipelineBlocks[0]?.id
   */
  onDeletePipelineBlock(block: RoutingPipelineBlock): void {
    this.store.pipelineBlocks = this.store.pipelineBlocks.filter((b) => b.id !== block.id);
    if (this.store.selectedPipelineId === block.id) {
      this.store.selectedPipelineId = this.store.pipelineBlocks[0]?.id ?? null;
    }
    this.store.patchBlocks([...this.store.pipelineBlocks]);
  }

  /**
   * Opens the expression builder dialog for a pipeline block's rule satisfaction expression.
   * @example block.ruleSatisfaction '' → Expression Builder opens; on save → block.ruleSatisfaction = '(PID.5 = "Smith")'
   */
  onBuildExpression(block: RoutingPipelineBlock): void {
    const resolved =
      this.store.pipelineBlocks.find((b) => b.id === block.id) ?? block;
    this.expression.openForBlock(resolved);
  }

  /**
   * Closes the routing rule configuration dialog without saving.
   * @example user clicks Cancel → dialogRef.close() dismisses MatDialog
   */
  closeDialog(): void {
    this.dialogRef?.close();
  }

  /**
   * Persists pipeline blocks and rules through the version service save flow.
   * @example user clicks Save → version.onClickSave() writes routingRuleConfig to backend
   */
  onClickSave(): void {
    this.version.onClickSave();
  }

  ngOnDestroy(): void {
    this.matSelectOverlay.destroy();
  }

  onVersionSelectOpenedChange(opened: boolean): void {
    this.matSelectOverlay.onOpenedChange(opened, this.versionSelectRef);
  }

  onCompareVersionSelectOpenedChange(opened: boolean, select: MatSelect): void {
    this.matSelectOverlay.onOpenedChange(opened, select);
  }
}
